/**
 * Finds how far this device's clock is from the server's, so the app can use
 * server time everywhere and never a student's device clock, which can be
 * months off. The `getServerTime` callable is the source; if it fails twice,
 * the hosting server's `Date` response header (whole seconds) stands in.
 */

/** How many times the `getServerTime` callable is tried before the fallback. */
export const SERVER_TIME_CALL_ATTEMPTS = 2;

/**
 * How long one clock read may take before it counts as failed. The callable's
 * own timeout is far longer, and the course page waits on the clock.
 */
export const CLOCK_READ_TIMEOUT_MS = 5_000;

/** The first wait before reading the clock again after every source failed. */
export const CLOCK_RETRY_FIRST_MS = 10_000;

/** The longest wait between clock retries; the course page waits on them. */
export const CLOCK_RETRY_MAX_MS = 30_000;

/**
 * Settles with `promise`, or rejects once `ms` pass without an answer.
 *
 * @param promise - The read to bound.
 * @param ms - The limit.
 * @return The read's value.
 * @throws Error when the limit passes first, or the read's own error.
 */
async function withTimeout<T>(promise: Promise<T>, ms: number): Promise<T> {
  let timer: ReturnType<typeof setTimeout> | undefined;
  const limit = new Promise<never>((_, reject) => {
    timer = setTimeout(() => reject(new Error(`clock read timed out after ${ms} ms`)), ms);
  });
  try {
    return await Promise.race([promise, limit]);
  } finally {
    clearTimeout(timer);
  }
}

/**
 * How long to wait before reading the clock again after `failures` rounds in
 * which every source failed: doubling from {@link CLOCK_RETRY_FIRST_MS}, at
 * most {@link CLOCK_RETRY_MAX_MS}.
 *
 * @param failures - Failed rounds so far, from 1.
 * @return The wait in ms.
 * @throws Never.
 */
export function clockRetryDelayMs(failures: number): number {
  const doublings = Math.max(0, failures - 1);
  return Math.min(CLOCK_RETRY_FIRST_MS * 2 ** doublings, CLOCK_RETRY_MAX_MS);
}

/**
 * The server-minus-device clock offset in ms.
 *
 * @param params - Inputs.
 * @param params.callServerTime - Resolves to the server's epoch ms (the
 *     `getServerTime` callable).
 * @param params.fetchDateHeaderMs - Resolves to the hosting server's `Date`
 *     header in epoch ms, or undefined when it has none. Omitted:
 *     {@link fetchHostingDateMs}.
 * @param params.deviceNowMs - The device clock, read when each answer
 *     arrives. Omitted: `Date.now`.
 * @param params.timeoutMs - How long each read may take. Omitted:
 *     {@link CLOCK_READ_TIMEOUT_MS}.
 * @return The offset, or undefined when neither source answered in time.
 * @throws Never.
 */
export async function fetchServerTimeDelta({
  callServerTime,
  fetchDateHeaderMs = fetchHostingDateMs,
  deviceNowMs = () => Date.now(),
  timeoutMs = CLOCK_READ_TIMEOUT_MS,
}: {
  callServerTime: () => Promise<number>;
  fetchDateHeaderMs?: () => Promise<number | undefined>;
  deviceNowMs?: () => number;
  timeoutMs?: number;
}): Promise<number | undefined> {
  for (let attempt = 1; attempt <= SERVER_TIME_CALL_ATTEMPTS; attempt += 1) {
    try {
      const serverMs = await withTimeout(callServerTime(), timeoutMs);
      if (Number.isFinite(serverMs)) {
        return serverMs - deviceNowMs();
      }
      console.warn('[serverClock] getServerTime returned a non-finite time', {
        attempt,
        serverMs,
      });
    } catch (error) {
      console.warn('[serverClock] getServerTime failed', {attempt, error});
    }
  }
  try {
    const headerMs = await withTimeout(fetchDateHeaderMs(), timeoutMs);
    if (headerMs !== undefined && Number.isFinite(headerMs)) {
      return headerMs - deviceNowMs();
    }
  } catch (error) {
    console.warn('[serverClock] hosting Date header unavailable', error);
  }
  return undefined;
}

/**
 * The hosting server's current time from the `Date` header of a same-origin
 * `HEAD` request.
 *
 * @return Epoch ms, or undefined when the response has no usable header.
 * @throws When the request itself fails.
 */
export async function fetchHostingDateMs(): Promise<number | undefined> {
  const response = await fetch(`${window.location.origin}/`, {
    method: 'HEAD',
    cache: 'no-store',
  });
  const header = response.headers.get('Date');
  const ms = header === null ? NaN : Date.parse(header);
  return Number.isFinite(ms) ? ms : undefined;
}
